import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsEmail, AtsEmailAccount, timeAgo } from './api';
import { MailboxConnectCard, btnPrimary, inputCls } from './shared';

// Workspace > Inbox. EVERYTHING syncs — a referral from a brand-new
// prospect or a reply to outreach must never be missed — and Jev separates
// signal from noise: unknown senders are classified, real correspondence
// shows here with one-click "Add to ATS", the rest lands under Filtered.
const FILTERS = [
  { key: 'awaiting', label: 'Awaiting reply' },
  { key: 'inbox', label: 'Inbox' },
  { key: 'outbox', label: 'Outbox' },
  { key: 'filtered', label: 'Filtered' },
  { key: 'all', label: 'All' },
] as const;

// Jev's tags: relevant ones get coloured chips, filtered ones grey chips.
const RELEVANT_SET = new Set(['human_business', 'client_lead', 'candidate_message', 'personal_reply']);
const CLASS_CHIP: Record<string, { label: string; cls: string }> = {
  client_lead: { label: 'Lead', cls: 'bg-emerald-50 text-emerald-700' },
  candidate_message: { label: 'Candidate', cls: 'bg-blue-50 text-blue-600' },
  cold_pitch: { label: 'Cold pitch', cls: 'bg-gray-100 text-gray-500' },
  automated_notification: { label: 'Notification', cls: 'bg-gray-100 text-gray-500' },
  newsletter_marketing: { label: 'Marketing', cls: 'bg-gray-100 text-gray-500' },
  junk: { label: 'Junk', cls: 'bg-red-50 text-red-500' },
};

const threadName = (t: AtsEmail) => t.person_name || t.counterpart_name || t.counterpart_address || 'Unknown';
const threadKey = (t: AtsEmail) => `${t.person_id ?? `a:${t.counterpart_address}`}-${t.conversation_id}`;
const sameThread = (a: AtsEmail, b: AtsEmail) => threadKey(a) === threadKey(b);

const AtsInbox: React.FC = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [configured, setConfigured] = useState(true);
  const [account, setAccount] = useState<AtsEmailAccount | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('awaiting');
  const [threads, setThreads] = useState<AtsEmail[]>([]);
  const [active, setActive] = useState<AtsEmail | null>(null);
  const [messages, setMessages] = useState<AtsEmail[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [error, setError] = useState(searchParams.get('emailError') || '');

  const flashSyncMsg = (msg: string) => {
    setSyncMsg(msg);
    window.setTimeout(() => setSyncMsg(''), 8000);
  };

  const loadStatus = useCallback(() => {
    if (!token) return;
    atsFetch(token, '/email/status')
      .then((d) => { setConfigured(d.configured); setAccount(d.account); })
      .catch(() => {})
      .finally(() => setStatusLoaded(true));
  }, [token]);

  useEffect(loadStatus, [loadStatus]);

  const loadThreads = useCallback(() => {
    if (!token || !account) return;
    atsFetch(token, `/inbox?filter=${filter}`)
      .then((d) => setThreads(d.threads))
      .catch(() => {});
  }, [token, account, filter]);

  useEffect(loadThreads, [loadThreads]);

  const openThread = async (t: AtsEmail) => {
    if (!token) return;
    setActive(t); setReply('');
    const qs = new URLSearchParams({ conversationId: t.conversation_id });
    if (t.person_id) qs.set('personId', String(t.person_id));
    else qs.set('counterpart', t.counterpart_address || '');
    const d = await atsFetch(token, `/inbox/thread?${qs}`).catch(() => null);
    if (d) setMessages(d.messages);
  };

  const sendReply = async () => {
    if (!token || !active || !reply.trim() || sending) return;
    const target = [...messages].reverse().find((m) => m.direction === 'inbound') || messages[messages.length - 1];
    if (!target) return;
    setSending(true);
    try {
      await atsFetch(token, '/inbox/reply', { method: 'POST', body: JSON.stringify({ emailId: target.id, bodyText: reply.trim() }) });
      setReply('');
      openThread(active);
      loadThreads();
    } catch (e: any) { setError(e.message); }
    setSending(false);
  };

  const addToAts = async (as: 'contact' | 'candidate') => {
    if (!token || !active || !active.counterpart_address || adding) return;
    setAdding(true);
    try {
      const d = await atsFetch(token, '/inbox/add-to-ats', {
        method: 'POST',
        body: JSON.stringify({ counterpart: active.counterpart_address, as }),
      });
      flashSyncMsg(d.client
        ? `Added ${d.person.name} as a contact at ${d.client.name} (${d.client.company_id ? 'matched from your mapped companies' : 'client created from their domain'}).`
        : `Added ${d.person.name} as a ${as}.`);
      setActive({ ...active, person_id: d.person.id, person_name: d.person.name, person_company: d.client ? d.client.name : active.person_company });
      loadThreads();
      openThread({ ...active, person_id: d.person.id });
    } catch (e: any) { setError(e.message); }
    setAdding(false);
  };

  const reclassify = async (t: AtsEmail, classification: string) => {
    if (!token || !t.counterpart_address) return;
    await atsFetch(token, '/inbox/classify', {
      method: 'POST',
      body: JSON.stringify({ counterpart: t.counterpart_address, classification }),
    }).catch((e: any) => setError(e.message));
    if (active && sameThread(active, t)) setActive(null);
    loadThreads();
  };

  const syncNow = async () => {
    if (!token || syncing) return;
    setSyncing(true);
    try {
      const d = await atsFetch(token, '/email/sync', { method: 'POST' });
      const mails = d.mailStored || 0;
      const meets = d.meetingsStored || 0;
      flashSyncMsg(mails || meets
        ? `Synced — ${mails} new message${mails === 1 ? '' : 's'}, ${meets} meeting${meets === 1 ? '' : 's'}.`
        : 'Up to date — nothing new since the last sync (it also runs automatically every 10 minutes).');
      loadStatus(); loadThreads();
      if (active) openThread(active);
    } catch (e: any) { setError(e.message); }
    setSyncing(false);
  };

  const retag = async () => {
    if (!token) return;
    if (!window.confirm('Re-judge every unknown-sender conversation with Jev using full message bodies and the current tags (Lead / Candidate / Cold pitch / …)? Runs in the background; your manual corrections may be overwritten.')) return;
    try {
      const d = await atsFetch(token, '/email/retag', { method: 'POST' });
      flashSyncMsg(`Re-tagging ${d.conversations} conversations in the background — refresh in a minute or two.`);
      window.setTimeout(loadThreads, 20000);
    } catch (e: any) { setError(e.message); }
  };

  const backfill = async () => {
    if (!token) return;
    const input = window.prompt('Backfill how many days of mailbox history? (max 365)\n\nJev classifies each unknown conversation once — even 6 months costs pennies. A big mailbox takes a few minutes; threads appear as they sync.', '180');
    if (!input) return;
    const days = parseInt(input, 10);
    if (!days || days < 1) return;
    try {
      await atsFetch(token, '/email/backfill', { method: 'POST', body: JSON.stringify({ days: Math.min(days, 365) }) });
      setError('');
      flashSyncMsg(`Backfilling ${Math.min(days, 365)} days in the background — threads appear as they sync (a big mailbox takes a few minutes).`);
      window.setTimeout(() => { loadStatus(); loadThreads(); }, 15000);
    } catch (e: any) { setError(e.message); }
  };

  const disconnect = async () => {
    if (!token) return;
    if (!window.confirm('Disconnect this mailbox? Synced history stays; new mail stops syncing until you reconnect.')) return;
    await atsFetch(token, '/email/disconnect', { method: 'POST' }).catch(() => {});
    setAccount(null); setThreads([]); setActive(null);
  };

  if (!statusLoaded) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;
  if (!account) return <MailboxConnectCard configured={configured} error={error || null} />;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === f.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{account.address}{account.last_synced_at ? ` · synced ${timeAgo(account.last_synced_at)}` : ' · first sync running…'}</span>
          {account.status === 'error' && <span className="text-red-500" title={account.last_error || ''}>sync error</span>}
          <button onClick={syncNow} disabled={syncing} className="text-emerald-700 hover:text-emerald-800 font-medium">{syncing ? 'Syncing…' : 'Sync now'}</button>
          <button onClick={backfill} className="hover:text-gray-600">Backfill…</button>
          <button onClick={retag} className="hover:text-gray-600">Re-tag</button>
          <button onClick={disconnect} className="hover:text-gray-600">Disconnect</button>
        </div>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {syncMsg && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">{syncMsg}</p>}

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Thread list */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden self-start">
          {threads.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10 px-4">
              {filter === 'awaiting' ? 'Nothing awaiting a reply.'
                : filter === 'filtered' ? 'Nothing filtered out yet — Jev parks notifications, marketing and junk here.'
                : 'No conversations here yet.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-50 max-h-[65vh] overflow-y-auto">
              {threads.map((t) => (
                <li key={threadKey(t)}>
                  <button
                    onClick={() => openThread(t)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${active && sameThread(active, t) ? 'bg-emerald-50/60' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate flex-1">{threadName(t)}</span>
                      {!t.person_id && RELEVANT_SET.has(t.classification) && !CLASS_CHIP[t.classification] && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 uppercase flex-shrink-0">New</span>
                      )}
                      {!t.person_id && CLASS_CHIP[t.classification] && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase flex-shrink-0 ${CLASS_CHIP[t.classification].cls}`}>{CLASS_CHIP[t.classification].label}</span>
                      )}
                      {t.thread_needs_reply && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Awaiting your reply" />}
                      <span className="text-[10px] text-gray-400">{timeAgo(t.sent_at)}</span>
                    </div>
                    <p className="text-xs text-gray-600 truncate mt-0.5">{t.subject || '(no subject)'}</p>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{t.preview}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread view */}
        <div className="lg:col-span-3">
          {!active ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center text-sm text-gray-400">
              Select a conversation.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col max-h-[72vh]">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                {active.person_id ? (
                  <Link to={`/ats/people/${active.person_id}`} className="text-sm font-semibold text-gray-900 hover:text-blue-700">{threadName(active)}</Link>
                ) : (
                  <span className="text-sm font-semibold text-gray-900">{threadName(active)}</span>
                )}
                {active.person_company && <span className="text-xs text-gray-400">{active.person_company}</span>}
                {!active.person_id && (
                  <span className="flex items-center gap-1.5 ml-1">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 uppercase">Not in ATS</span>
                    <button onClick={() => addToAts('contact')} disabled={adding} className="px-2 py-1 rounded-md bg-violet-600 text-white text-[11px] font-medium hover:bg-violet-700 disabled:opacity-50">+ Contact</button>
                    <button onClick={() => addToAts('candidate')} disabled={adding} className="px-2 py-1 rounded-md bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 disabled:opacity-50">+ Candidate</button>
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  {!active.person_id && RELEVANT_SET.has(active.classification) && (
                    <button onClick={() => reclassify(active, 'junk')} className="text-[11px] text-gray-400 hover:text-red-500">Filter out</button>
                  )}
                  {!active.person_id && !RELEVANT_SET.has(active.classification) && (
                    <button onClick={() => reclassify(active, 'human_business')} className="text-[11px] text-emerald-700 hover:text-emerald-800 font-medium">Not junk — move to Inbox</button>
                  )}
                  <span className="text-xs text-gray-400 truncate max-w-[220px]">{active.subject || '(no subject)'}</span>
                </span>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`max-w-[85%] ${m.direction === 'outbound' ? 'ml-auto' : ''}`}>
                    <div className={`rounded-xl px-3.5 py-2.5 text-sm ${m.direction === 'outbound' ? 'bg-emerald-50 text-gray-800' : 'bg-gray-50 text-gray-800'}`}>
                      {m.subject && m.subject !== active.subject && <p className="text-xs font-medium text-gray-500 mb-1">{m.subject}</p>}
                      <p className="whitespace-pre-wrap break-words">{m.body_text || m.preview || '(no preview)'}</p>
                    </div>
                    <p className={`text-[10px] text-gray-400 mt-0.5 ${m.direction === 'outbound' ? 'text-right' : ''}`}>
                      {m.direction === 'outbound' ? 'You' : threadName(active)} · {timeAgo(m.sent_at)}
                      {m.needs_reply && <span className="text-amber-500 font-medium"> · awaiting reply</span>}
                    </p>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 px-4 py-3 flex gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={`Reply to ${threadName(active)}… (sends from ${account.address})`}
                  rows={2}
                  className={inputCls}
                />
                <button onClick={sendReply} disabled={sending || !reply.trim()} className={btnPrimary}>{sending ? 'Sending…' : 'Reply'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-3">Everything syncs; Jev files notifications, marketing and junk under Filtered (rescue anything with "Not junk"). Synced messages store subject + preview only — open Outlook for full bodies and attachments. Replies send through your connected mailbox.</p>
    </div>
  );
};

export default AtsInbox;
